// EntityListView.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Box, Grid, Card, CardContent, Typography } from "@mui/material";
import EntityFilter from "./entity-filter";
import { useTheme } from "@mui/material/styles";
import EntityDetailsDialog from "./entity-dialog";
import { toEntityTitleSubtitle } from "../common/string-helpers";

const EntityCard = ({ meta, entity, theme, onSelect }) => {
  const entityInfo = toEntityTitleSubtitle(entity.name, true);
  const displayTitle =
    typeof entityInfo === "object" ? entityInfo.title : entityInfo;
  const displaySubtitle =
    typeof entityInfo === "object"
      ? entityInfo.subtitle
      : meta.integrationNames[entity.integration] || entity.integration;

  return (
    <Card
      elevation={0}
      sx={{
        height: 90,
        border: "none",
        borderRadius: 2,
        backgroundColor: theme.palette.background.card,
        transition: "all 0.2s ease-in-out",
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
        userSelect: "none",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "&:active": {
          backgroundColor: theme.palette.action.selected,
        },
        // Mobile-specific styles
        "@media (hover: none)": {
          "&:hover": {
            backgroundColor: theme.palette.background.paper,
          },
        },
        cursor: "pointer",
      }}
      onClick={() => onSelect(entity)}
    >
      <CardContent
        sx={{
          p: "12px !important",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            lineHeight: 1.2,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            height: "2.4em",
          }}
        >
          {displayTitle}
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          noWrap
          sx={{ mt: 0.5 }}
        >
          {displaySubtitle}
        </Typography>
      </CardContent>
    </Card>
  );
};

const EntityListView = ({ meta, entities, showFilter }) => {
  const [entityFilterText, setEntityFilterText] = useState(""); // Changed to useState
  const [entitySortBy, setEntitySortBy] = useState("alphabet");
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedIntegrations, setSelectedIntegrations] = useState([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);
  const theme = useTheme();

  // Moved the filtering and sorting logic into a useMemo to optimize performance
  const sortedEntities = useMemo(() => {
    return entities
      .filter((entity) => {
        // Text filter
        const searchTerms = entityFilterText
          .toLowerCase()
          .split(" ")
          .filter((term) => term.length > 0);
        const searchableText = entity.name.toLowerCase();
        const matchesText = !entityFilterText
          ? true
          : searchTerms.every((term) => searchableText.includes(term));

        // Integration filter
        const matchesIntegration =
          selectedIntegrations.length === 0 ||
          selectedIntegrations.includes(entity.integration);

        // Capability filter
        const matchesCapability =
          selectedCapabilities.length === 0 ||
          (entity.capabilities &&
            selectedCapabilities.some((capability) =>
              entity.capabilities.includes(capability)
            ));

        return matchesText && matchesIntegration && matchesCapability;
      })
      .sort((a, b) => {
        if (entitySortBy === "alphabet") {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          const aIsLetter = /^[a-zA-Z]/.test(aName);
          const bIsLetter = /^[a-zA-Z]/.test(bName);
          if (aIsLetter && !bIsLetter) return -1;
          if (!aIsLetter && bIsLetter) return 1;
          return aName.localeCompare(bName);
        }
        return 0;
      });
  }, [
    entities,
    entityFilterText,
    entitySortBy,
    selectedIntegrations,
    selectedCapabilities,
  ]);

  // Debug logging to check if capabilities are being filtered correctly
  useEffect(() => {
    console.log("Selected capabilities:", selectedCapabilities);
    console.log("Filtered entities count:", sortedEntities.length);
  }, [selectedCapabilities, sortedEntities.length]);

  return (
    <Box>
      {showFilter && (
        <EntityFilter
          sortBy={entitySortBy}
          setSortBy={setEntitySortBy}
          setFilterText={setEntityFilterText}
          meta={meta}
          selectedIntegrations={selectedIntegrations}
          setSelectedIntegrations={setSelectedIntegrations}
          selectedCapabilities={selectedCapabilities}
          setSelectedCapabilities={setSelectedCapabilities}
          entities={entities}
        />
      )}
      <Grid container spacing={1.5}>
        {sortedEntities.map((entity) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={entity.id}>
            <EntityCard
              meta={meta}
              entity={entity}
              theme={theme}
              onSelect={setSelectedEntity}
            />
          </Grid>
        ))}
      </Grid>
      <EntityDetailsDialog
        meta={meta}
        open={Boolean(selectedEntity)}
        entity={selectedEntity}
        onClose={() => setSelectedEntity(null)}
      />
    </Box>
  );
};

export default EntityListView;
